import {Injectable} from '@angular/core';

@Injectable({providedIn: 'root'})
export class FilterPersistenceService
{
    private filters: Record<string, any> = {};
    private STORAGE_KEY_PREFIX = 'dashboard-filters-';

    /**
     * Initializes the FilterPersistenceService and loads filters from local storage.
     */
    constructor()
    {
        this.loadFiltersFromStorage();
    }

    /**
     * Retrieves the filters for a specific route.
     *
     * @param route - The route for which to get the filters.
     * @returns The filters associated with the route, or null if none exist.
     */
    getFilters(route: string): any
    {
        return this.filters[route] || null;
    }

    /**
     * Sets new filters for a specific route and saves them to local storage.
     *
     * @param route - The route for which to set the filters.
     * @param newFilters - The new filters to set.
     */
    setFilters(route: string, newFilters: any): void
    {
        this.filters[route] = newFilters;
        localStorage.setItem(this.getStorageKey(route), JSON.stringify(newFilters));
    }

    /**
     * Clears all filters across all routes and removes them from local storage.
     */
    clearAllFilters(): void
    {
        this.filters = {};
        Object.keys(localStorage).forEach((key) =>
        {
            if (key.startsWith(this.STORAGE_KEY_PREFIX))
            {
                localStorage.removeItem(key);
            }
        });
    }

    /**
     * Clears the filters for a specific route and removes them from local storage.
     *
     * @param route - The route for which to clear the filters.
     */
    clearFiltersForRoute(route: string): void
    {
        delete this.filters[route];
        localStorage.removeItem(this.getStorageKey(route));
    }

    /**
     * Generates a storage key for a specific route based on the STORAGE_KEY_PREFIX.
     *
     * @param route - The route for which to generate the storage key.
     * @returns The storage key for the specified route.
     */
    private getStorageKey(route: string): string
    {
        return this.STORAGE_KEY_PREFIX + route;
    }

    /**
     * Loads existing filters from local storage into memory.
     */
    private loadFiltersFromStorage(): void
    {
        Object.keys(localStorage).forEach((key) =>
        {
            if (key.startsWith(this.STORAGE_KEY_PREFIX))
            {
                const route = key.replace(this.STORAGE_KEY_PREFIX, '');
                this.filters[route] = JSON.parse(localStorage.getItem(key) || '{}');
            }
        });
    }
}